/**
 * Entrada real da triagem → card da esteira (TP-025).
 *
 * Só dado de negócio: nada de `.eml`, anexo ou assunto — quem opera a
 * captação não sabe o que é um `.eml` (Breno, 24/09).
 */

import { formatarHora } from '../../../shared/dominio/datas';
import type { AneryCard } from '../../../shared/ui/tiposDaEsteira';
import { classificacaoIncerta, textoDaModalidade, type CampoDaTriagem } from '../../../shared/triagem/tipos';
import type { EntradaNaCaptacao } from './api/entradasDaCaptacao';

/** A correção humana vence a leitura da máquina, como na fila. */
function lido(campo: CampoDaTriagem): string | null {
  const valor = campo.valorHumano ?? campo.valor;
  return valor !== null && valor.trim() !== '' ? valor : null;
}

/**
 * A modalidade do card é a que a classificação indica (a mesma da fila, `TP-042` D3), e não o tipo cru: o tipo
 * continua no subtítulo. Sem classificação ainda, ou com um tipo que não indica modalidade (PAD, NEAD), o card diz
 * isso em vez de repetir o tipo.
 */
function modalidadeDoCard(ticket: EntradaNaCaptacao['ticket']): string {
  if (ticket.classificacao === null) return 'modalidade ainda não identificada';
  return textoDaModalidade(ticket.classificacao) ?? 'modalidade não indicada pelo tipo';
}

export function mapearEntradaParaCard(entrada: EntradaNaCaptacao): AneryCard {
  const { ticket } = entrada;
  const codigo = ticket.codigoCurto ?? 'sem código';
  const tipo = lido(ticket.tipoDeSolicitacao) ?? 'tipo de solicitação não lido';
  const hospital = lido(ticket.hospital);

  return {
    // A esteira indexa o estado por título: o código curto separa dois
    // pacientes com o mesmo nome.
    titulo: `${lido(ticket.paciente) ?? 'paciente não lido'} · ${codigo}`,
    operadora: lido(ticket.operadora) ?? undefined,
    sub: tipo,
    meta: `chegou por e-mail · ${formatarHora(entrada.entrouEm)}`,
    detalhe: {
      modalidade: modalidadeDoCard(ticket),
      canal: `e-mail lido pela triagem · ticket ${codigo}`,
      solicitacao: hospital === null ? 'hospital não lido' : `hospital: ${hospital}`,
      // O caso fica em "Novo" (a esteira não anda com caso real), então o
      // momento diz onde ele está de verdade.
      momento: {
        tipo: 'recebido',
        texto: 'O e-mail chegou e a triagem já leu os campos.',
        acao: 'Aguardando o próximo passo da captação.',
      },
    },
    // TP-028 T5: só quem passou pelo portão sem certeza (Undetermined/null)
    // carrega a flag. `Receipt` (o único outro caso que chega aqui — o
    // portão já bloqueia InProgress/OutOfProcess antes da captação) não.
    classificacaoIncerta: classificacaoIncerta(ticket.tipoDeMensagem),
  };
}
